import { useState, useEffect } from "react";
import { Box, Typography, Skeleton, Tooltip, IconButton } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import apiService from "../../../service/Api";
import {
  getCache,
  setCache,
  type ComponentManager,
  type Developer,
} from "./AssignmentsCache";
import figmaLogoPng from "../../../assets/figmalogo.png";

const deriveComponentName = (pathname: string) => {
  const segments = pathname.split("/").filter(Boolean);
  return segments.slice(0, 2).join("/");
};

const getDeveloperName = (dev?: Developer) => {
  if (!dev) return "Not Assigned";
  const full = [dev.first_name, dev.last_name].filter(Boolean).join(" ");
  return full || dev.username;
};

const normalise = (s: string) => s.toLowerCase().replace(/[\s\-_/]/g, "");

const fetchAllManagers = async (): Promise<ComponentManager[]> => {
  const cached = getCache();
  if (cached) return cached;

  let all: ComponentManager[] = [];
  let url: string | null = "/api/configurations/components-managers/";

  while (url) {
    const response: {
      data:
        | { results?: ComponentManager[]; next?: string | null }
        | ComponentManager[];
    } = await apiService.get(url);

    if (Array.isArray(response.data)) {
      all = response.data;
      break;
    } else {
      const paginated = response.data as {
        results?: ComponentManager[];
        next?: string | null;
      };
      all = [...all, ...(paginated.results || [])];
      const next = paginated.next ?? null;
      if (next) {
        const urlObj = new URL(next);
        url = urlObj.pathname + urlObj.search;
      } else {
        url = null;
      }
    }
  }

  setCache(all);
  return all;
};

const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [managers, setManagers] = useState<ComponentManager[]>(
    getCache() || [],
  );
  const [loading, setLoading] = useState(!getCache());

  useEffect(() => {
    const cached = getCache();
    if (cached) {
      setManagers(cached);
      setLoading(false);
      return;
    }
    const fetchManagers = async () => {
      setLoading(true);
      try {
        const data = await fetchAllManagers();
        setManagers(data);
      } catch (error) {
        console.error("Failed to fetch component managers:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchManagers();
  }, []);

  // Listen for assignments-updated event dispatched by UpdateFooter after mutations
  useEffect(() => {
    const handleUpdate = async () => {
      setLoading(true);
      try {
        const data = await fetchAllManagers();
        setManagers(data);
      } catch (error) {
        console.error("Failed to refresh component managers:", error);
      } finally {
        setLoading(false);
      }
    };

    window.addEventListener("assignments-updated", handleUpdate);
    return () =>
      window.removeEventListener("assignments-updated", handleUpdate);
  }, []);

  const componentName = deriveComponentName(location.pathname);
  const baseComponentName = location.pathname.split("/").filter(Boolean)[0];

  const match =
    managers.find(
      (m) => normalise(m.component.name) === normalise(componentName),
    ) ||
    managers.find(
      (m) => normalise(m.component.name) === normalise(baseComponentName),
    );

  const whatsappUrl = match?.whatsapp_group_url ?? null;
  const figmaUrl = match?.figma_url ?? null;

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 1,
          borderTop: "1px solid #e0e0e0",
          mt: 2,
          backgroundColor: "background.paper",
        }}
      >
        <Box sx={{ width: "100%" }}>
          <Skeleton width={160} height={20} />
          <Skeleton width={300} height={16} />
        </Box>
      </Box>
    );
  }

  const handleFooterClick = () => {
    navigate("/configurations/components-manager", {
      state: {
        componentName: match?.component.name ?? componentName,
        componentId: match?.component.id ?? null,
      },
    });
  };

  const handleWhatsAppClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // prevent footer's navigate from firing
    window.open(whatsappUrl!, "_blank", "noopener,noreferrer");
  };

  const handleFigmaClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // prevent footer's navigate from firing
    window.open(figmaUrl!, "_blank", "noopener,noreferrer");
  };

  return (
    <Tooltip
      title={
        match
          ? "Click to view or edit this component's assignment"
          : "Click to assign a lead to this component"
      }
      placement="top"
    >
      <Box
        onClick={handleFooterClick}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 1,
          borderTop: "1px solid #e0e0e0",
          mt: 2,
          backgroundColor: "background.paper",
          cursor: "pointer",
          transition: "background-color 0.2s",
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography sx={{ fontWeight: "bold" }}>
              {match?.component.name ?? componentName}
            </Typography>
            {whatsappUrl && (
              <Tooltip title="Open WhatsApp group" placement="top">
                <IconButton
                  onClick={handleWhatsAppClick}
                  size="small"
                  sx={{
                    color: "#fff",
                    backgroundColor: "#25D366",
                    p: 0.5,
                    borderRadius: "4px",
                    "&:hover": {
                      backgroundColor: "#128C7E",
                      transform: "scale(1.1)",
                      transition: "all 0.2s",
                    },
                  }}
                >
                  <WhatsAppIcon sx={{ fontSize: 18 }} />{" "}
                  {/* Increased from 16 */}
                </IconButton>
              </Tooltip>
            )}

            {figmaUrl && (
              <Tooltip title="Open Figma design" placement="top">
                <IconButton
                  onClick={handleFigmaClick}
                  size="small"
                  sx={{
                    p: 0,
                    backgroundColor: "transparent",
                    "&:hover": {
                      backgroundColor: "transparent",
                      transform: "scale(1.15)",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  <Box
                    component="img"
                    src={figmaLogoPng}
                    alt="Figma"
                    sx={{
                      width: 30,
                      height: 30,
                      display: "block",
                      objectFit: "contain",
                    }}
                  />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Typography variant="body2">
            {match
              ? `Lead: ${getDeveloperName(match.lead_developer)} | Backup: ${getDeveloperName(match.backup_developer)}`
              : "Not assigned — click to assign a lead"}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {match?.version && (
            <Typography variant="caption" color="text.secondary">
              v{match.version}
            </Typography>
          )}
        </Box>
      </Box>
    </Tooltip>
  );
};

export default Footer;
